import { Helmet } from "react-helmet-async";

const SITE_NAME = "LoyltyKart";
const SITE_URL = "https://loyltykart.com";
const DEFAULT_TITLE = "LoyltyKart — Luxury Rewards Marketplace | 270+ Brand Gift Cards, Instant Delivery";
const DEFAULT_DESCRIPTION =
    "India's luxury rewards marketplace. Buy instant digital gift cards from 270+ brands like Tanishq, MakeMyTrip, Nykaa, Amazon, Croma, Taj & more. Delivered in 10 seconds via WhatsApp with exclusive discounts.";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

export default function SEO({
    title,
    description = DEFAULT_DESCRIPTION,
    path = "",
    image = DEFAULT_IMAGE,
    type = "website",
    structuredData = null,
    noindex = false,
    keywords = null,
}) {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
    const url = `${SITE_URL}${path || ""}`;
    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={url} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
            {/* Open Graph */}
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:locale" content="en_IN" />
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            {/* Schema.org structured data */}
            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData])}
                </script>
            )}
        </Helmet>
    );
}

export const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    sameAs: [
        "https://twitter.com/loyltykart",
        "https://instagram.com/loyltykart",
        "https://www.linkedin.com/company/loyltykart",
    ],
    contactPoint: [{
        "@type": "ContactPoint",
        telephone: "+91-1800-123-4567",
        contactType: "customer support",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
    }],
};

export const siteSearchSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/catalog?q={search_term_string}`,
        "query-input": "required name=search_term_string",
    },
};

export const breadcrumbSchema = (items) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: `${SITE_URL}${it.path}`,
    })),
});

export const productSchema = (brand) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${brand.brand_name} Gift Card`,
    image: brand.logo_url,
    description:
        brand.description ||
        `${brand.brand_name} gift card — instant digital delivery. Use at any ${brand.brand_name} store or online. ${brand.validity || "1 Year"} validity.`,
    brand: { "@type": "Brand", name: brand.brand_name },
    sku: brand.sku,
    category: brand.category,
    offers: {
        "@type": "AggregateOffer",
        priceCurrency: "INR",
        lowPrice: brand.min_denomination,
        highPrice: brand.max_denomination,
        offerCount: (brand.suggested_denominations || []).length || 1,
        availability: "https://schema.org/InStock",
        seller: { "@type": "Organization", name: SITE_NAME },
    },
    aggregateRating: brand.rating
        ? {
              "@type": "AggregateRating",
              ratingValue: brand.rating,
              reviewCount: Math.round((brand.popularity || 50) * 3),
          }
        : undefined,
});

export const faqSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
});

export const articleSchema = (post) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover || DEFAULT_IMAGE,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` },
    },
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
});

export { SITE_NAME, SITE_URL };
